import { Component, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from './services/auth.service';
import { LogService } from './services/log.service';
import { UserSessionService } from './services/user-session.service';
import { LanguageService } from './services/language.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'ControlPS';
  useControlchatV2 = true;
  showGlobalChatWidget = true;
  private currentUser: any = null;
  private readonly idleTimeoutMs = 60 * 60 * 1000;
  private idleTimerId: number | null = null;
  private activityHandler = () => this.registerActivity();
  private visibilityHandler = () => this.onVisibilityChange();

  constructor(
    private titleService: Title,
    private userSession: UserSessionService,
    private auth: AuthService,
    private router: Router,
    private logService: LogService,
    private languageService: LanguageService
  ) {}

  ngOnInit() {
    this.languageService.init();

    this.userSession.user$.subscribe((user) => {
      this.currentUser = user;
      this.applyTitle();
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
      const url = (event as NavigationEnd).urlAfterRedirects || '';
      this.showGlobalChatWidget = !url.startsWith('/controlchat-desktop');
      this.applyTitle();
    });

    this.setupIdleTracking();
  }

  ngOnDestroy() {
    this.detachActivityListeners();
    if (this.idleTimerId !== null) {
      window.clearTimeout(this.idleTimerId);
      this.idleTimerId = null;
    }
  }

  private setupIdleTracking() {
    this.attachActivityListeners();
    document.addEventListener('visibilitychange', this.visibilityHandler);
    this.ensureSessionValidity();
    this.registerActivity();
  }

  private attachActivityListeners() {
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((evt) => window.addEventListener(evt, this.activityHandler, { passive: true }));
  }

  private detachActivityListeners() {
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((evt) => window.removeEventListener(evt, this.activityHandler));
    document.removeEventListener('visibilitychange', this.visibilityHandler);
  }

  private onVisibilityChange() {
    if (document.visibilityState === 'visible') {
      this.ensureSessionValidity();
    }
  }

  private ensureSessionValidity() {
    if (!this.auth.isLoggedIn()) {
      return;
    }
    const last = this.getLastActivity();
    if (last && Date.now() - last >= this.idleTimeoutMs) {
      this.forceLogoutForInactivity();
    }
  }

  private registerActivity() {
    if (!this.auth.isLoggedIn()) {
      return;
    }
    this.setLastActivity(Date.now());
    this.resetIdleTimer();
  }

  private resetIdleTimer() {
    if (this.idleTimerId !== null) {
      window.clearTimeout(this.idleTimerId);
    }
    this.idleTimerId = window.setTimeout(() => this.handleIdleTimeout(), this.idleTimeoutMs);
  }

  private handleIdleTimeout() {
    if (!this.auth.isLoggedIn()) {
      return;
    }
    const last = this.getLastActivity();
    if (!last || Date.now() - last >= this.idleTimeoutMs) {
      this.forceLogoutForInactivity();
      return;
    }
    const remaining = Math.max(0, this.idleTimeoutMs - (Date.now() - last));
    this.idleTimerId = window.setTimeout(() => this.handleIdleTimeout(), remaining);
  }

  private forceLogoutForInactivity() {
    this.logService.register('LOGOUT_EXPIRACAO', 'Sessão expirada por inatividade').subscribe({
      error: () => {}
    });
    this.auth.logout();
    this.router.navigate(['/login'], { queryParams: { reason: 'expired' } });
  }

  private applyTitle() {
    const tenantName = this.currentUser?.tenant_name ?? this.currentUser?.tenant?.name ?? '';
    const displayName =
      this.currentUser?.name ||
      this.currentUser?.full_name ||
      this.currentUser?.username ||
      this.currentUser?.email ||
      '';
    const email = this.currentUser?.email || '';
    const isDesktop = this.isControlchatDesktop();
    if (isDesktop) {
      const parts = ['CPS', 'ControlChat'];
      if (tenantName) {
        parts.push(tenantName);
      }
      if (displayName) {
        parts.push(displayName);
      }
      if (email && email !== displayName) {
        parts.push(email);
      }
      this.titleService.setTitle(parts.join(' - '));
      return;
    }
    if (tenantName) {
      this.titleService.setTitle(`CPS - ${tenantName}`);
    } else {
      this.titleService.setTitle('CPS');
    }
  }

  private isControlchatDesktop(): boolean {
    const path = window.location.pathname || '';
    if (path.startsWith('/controlchat-desktop')) {
      return true;
    }
    return localStorage.getItem('controlchat_desktop_mode') === '1';
  }

  private setLastActivity(timestamp: number) {
    localStorage.setItem('last_activity_at', String(timestamp));
  }

  private getLastActivity(): number | null {
    const raw = localStorage.getItem('last_activity_at');
    if (!raw) {
      return null;
    }
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
  }
}
